import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { TABLE_ROW_DND_ITEM_TYPE } from './constants';

interface UseEditTableDragProps {
  canDrop: (input: { id: string; index: number }) => number;
  index: number;
  localId: string;
  moveRowDone: (localId: string, index: number) => void;
  onBeforeDrag?: () => void;
}

type ITableRowDndItem = {
  id: string;
  index: number;
};

let dndItemRef = undefined;

export const useEditTableDrag = <
  TDrop = HTMLDivElement,
  TDrag = HTMLDivElement
>(
  props: UseEditTableDragProps
) => {
  const { canDrop, index, localId, moveRowDone, onBeforeDrag } = props;

  const dropRef = useRef<TDrop>(null);
  const dragRef = useRef<TDrag>(null);

  const [{ isOver, direction }, drop] = useDrop({
    accept: TABLE_ROW_DND_ITEM_TYPE,
    canDrop: (item, monitor) => {
      const canDropValue = canDrop(monitor.getItem());
      return Boolean(canDropValue);
    },
    collect: (monitor) => {
      const canDropValue = canDrop(monitor.getItem());
      return {
        isOver: monitor.isOver() && canDropValue,
        direction: canDropValue > 0 ? 'up' : 'down',
      };
    },
    hover(item: ITableRowDndItem) {
      if (!dropRef.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      dndItemRef = { id: item.id, index: hoverIndex };
    },
  });

  const [{ isDragging }, drag, preview] = useDrag<
    ITableRowDndItem,
    unknown,
    { isDragging: boolean }
  >({
    type: TABLE_ROW_DND_ITEM_TYPE,
    item: () => {
      onBeforeDrag?.();
      return { id: localId, index };
    },
    options: {
      dropEffect: 'move',
    },
    collect(monitor) {
      return {
        isDragging: monitor.isDragging(),
      };
    },
    end() {
      if (dndItemRef) {
        moveRowDone?.(dndItemRef.id, dndItemRef.index);
      }
    },
  });

  preview(drop(dropRef));
  drag(dragRef);

  return { isOver, direction, isDragging, dropRef, dragRef };
};
